<template>
  <BaseCard
    v-if="showcase?.additional_questions?.length"
    style="scroll-margin-top: calc(4rem + var(--sat))"
  >
    <BaseForm
      ref="formComponent"
      class="space-y-5"
      :method="onFormSubmit"
      :reset-on-submit="false"
    >
      <div class="font-bold md:text-lg">
        {{ $t('showcases.additional_questions') }}
      </div>
      <BaseFormField
        v-for="question in showcase.additional_questions"
        :key="question.id"
        :required="question.is_required"
        is-column-view
      >
        <template #label>
          {{ getQuestionFieldText(question.question) }}
        </template>
        <template #input>
          <BaseInput
            v-if="question.type === ShowcaseAdditionalQuestionType.Input"
            v-model="singleAnswers[question.id]"
            :name="`additional_question_${question.id}`"
            :placeholder="getQuestionFieldText(question.placeholder)"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
          />
          <BaseContentEditable
            v-if="question.type === ShowcaseAdditionalQuestionType.Textarea"
            v-model="singleAnswers[question.id]"
            :name="`additional_question_${question.id}`"
            :placeholder="getQuestionFieldText(question.placeholder)"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
          />
          <BaseRadioGroup
            v-if="question.type === ShowcaseAdditionalQuestionType.Radio"
            v-model="singleAnswers[question.id]"
            :options="convertQuestionOptionsToInputOptions(question.options)"
            :name="`additional_question_${question.id}`"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
            :is-column-view="true"
          />
          <BaseCheckboxGroup
            v-if="question.type === ShowcaseAdditionalQuestionType.Checkbox"
            v-model="multiAnswers[question.id]"
            :options="convertQuestionOptionsToInputOptions(question.options)"
            :name="`additional_question_${question.id}`"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
            :is-column-view="true"
          />
          <BaseSelect
            v-if="question.type === ShowcaseAdditionalQuestionType.Select"
            v-model="singleAnswers[question.id]"
            :options="convertQuestionOptionsToInputOptions(question.options)"
            :name="`additional_question_${question.id}`"
            :placeholder="getQuestionFieldText(question.placeholder)"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
          />
          <BaseMultiSelect
            v-if="question.type === ShowcaseAdditionalQuestionType.MultiSelect"
            v-model="multiAnswers[question.id]"
            :options="convertQuestionOptionsToInputOptions(question.options)"
            :name="`additional_question_${question.id}`"
            :placeholder="getQuestionFieldText(question.placeholder)"
            :description="getQuestionFieldText(question.description)"
            :required="question.is_required"
            :disabled="props.disabled"
          />
        </template>
      </BaseFormField>
    </BaseForm>
  </BaseCard>
</template>

<script setup lang="ts">
import BaseForm from '~/components/base/BaseForm.vue'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'
import type { ShowcaseAdditionalQuestionOption } from '~/models/showcase-additional-questions'
import { ShowcaseAdditionalQuestionType } from '~/models/showcase-additional-questions'
import type { ShowcaseRequestAdditionalQuestionAnswer } from '~/models/showcase-requests'
import { useShowcases } from '~/stores/showcases'
import { useCurrentLang } from '~/stores/current-lang'

const { lang } = useCurrentLang()

interface Props {
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

defineExpose({
  getAnswers,
  validate,
})

const emit = defineEmits<{
  (e: 'submitRequest', value: void): void
}>()

const formComponent = ref<InstanceType<typeof BaseForm> | null>(null)

const { showcase } = storeToRefs(useShowcases())

const singleAnswers = ref<Record<number, string | number>>({})
const multiAnswers = ref<Record<number, number[]>>({})

function convertQuestionOptionsToInputOptions(
  answerOptions?: ShowcaseAdditionalQuestionOption[]
): OptionInterface[] {
  return (
    answerOptions?.map(({ id, label }) => ({
      value: id,
      label: getQuestionFieldText(label),
    })) || []
  )
}

function getQuestionFieldText(
  value: string | Record<string, string> | null
): string {
  return (value && getFieldLocale(value, lang)) || ''
}

function onFormSubmit(): void {
  emit('submitRequest')
}

async function validate(): Promise<boolean> {
  if (showcase.value?.additional_questions?.length) {
    return (await formComponent.value?.validate())?.valid || false
  } else {
    return true
  }
}

function getAnswers(): ShowcaseRequestAdditionalQuestionAnswer[] {
  const answers: Record<number, string | number | number[]> = {
    ...singleAnswers.value,
    ...multiAnswers.value,
  }

  return Object.entries(answers).map(([questionId, value]) => ({
    question_id: Number(questionId),
    value,
  }))
}
</script>
